import { useCallback, useContext, useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { fetchSeasonsPlayed, fetchTeamStanding } from "../../services/TeamService";
import PlayerImage from "../../components/PlayerImage";
import { admins, divisionMappings } from "../../Constants";
import useTeamData from "../../hooks/useTeamData";
import '../../styles/theme.css';
import './TeamPage.css'; // Import component-specific styles
import SeasonScroll from "../../components/SeasonScroll/SeasonScroll";
import TeamTabs from "./TeamTabs";
import TeamPlayers from "./TeamPlayers";
import TeamMatches from "./TeamMatches";
import { Standing } from "../../models/Standing";
import { Account } from "../../models/Account";
import { fetchAccount } from "../../services/AccountService";
import Loading from "../../components/Loading";
import { AuthContext } from "../../context/AuthContext";
import TeamMapStats from "./TeamMapStats";
import { Team } from "../../models/Team"; // Import Team type
import { PlayerStats } from "../../models/PlayerStats"; // Import PlayerStats type

const TeamPage = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const [searchParams] = useSearchParams();
  
  // State for team data
  const [season, setSeason] = useState<number>(0);
  const [seasons, setSeasons] = useState<number[]>([]);
  const [owner, setOwner] = useState<Account | null>(null);
  const [coach, setCoach] = useState<Account | null>(null);
  const [standing, setStanding] = useState<Standing>();
  const [activeTab, setActiveTab] = useState<string>("players");
  
  // Loading states
  const [initialLoadStarted, setInitialLoadStarted] = useState<boolean>(false);
  const [seasonsLoading, setSeasonsLoading] = useState<boolean>(true);
  const [seasonChangeLoading, setSeasonChangeLoading] = useState<boolean>(false);
  
  // Store previous content to prevent flash during loading - with correct types
  const [prevTeamData, setPrevTeamData] = useState<Team | null>(null);
  const [prevPlayers, setPrevPlayers] = useState<PlayerStats[]>([]);
  
  const { teamData, players, loading: teamDataLoading } = useTeamData(teamId, season);
  const { user: visitor } = useContext(AuthContext);
  
  // This tracks if we're still doing the first load of the page
  const isInitialLoading = !initialLoadStarted || seasonsLoading || (season === 0 && seasons.length > 0);

  // Save current data as previous data when season changes
  useEffect(() => {
    if (teamData && !teamDataLoading) {
      setPrevTeamData(teamData);
    }
    if (players && players.length > 0 && !teamDataLoading) {
      setPrevPlayers(players);
    }
  }, [teamData, players, teamDataLoading]);

  // Fetch all seasons the team has played
  const getSeasonsPlayed = useCallback(async () => {
    if (!teamId) return;
    
    try {
      setSeasonsLoading(true);
      console.log("Fetching seasons for team:", teamId);
      const fetchedSeasons = await fetchSeasonsPlayed(teamId);
      console.log("Fetched seasons:", fetchedSeasons);
      
      if (fetchedSeasons && fetchedSeasons.length > 0) {
        setSeasons(fetchedSeasons);
        setInitialLoadStarted(true);
      } else {
        console.error("No seasons found for team:", teamId);
      }
    } catch (error) {
      console.error("Error fetching seasons:", error);
    } finally {
      setSeasonsLoading(false);
    }
  }, [teamId]);

  // Fetch the team standing for the current season
  const getTeamStanding = useCallback(async () => {
    if (!teamId || season === 0) return;
    
    try {
      const teamStanding = await fetchTeamStanding(teamId, season);
      setStanding(teamStanding);
    } catch (error) {
      console.error("Error fetching team standing:", error);
    }
  }, [teamId, season]);

  // Once the team data is available, fetch the owner & coach info
  useEffect(() => {
    const getOwnerAndCoach = async () => {
      if (!teamData) {
        // Don't reset owner/coach during loading to prevent flashing
        return;
      }
      
      try {
        if (teamData.ownerId) {
          const owner = await fetchAccount(teamData.ownerId);
          setOwner(owner || null);
        }

        if (teamData.coachID) {
          const coach = await fetchAccount(teamData.coachID);
          setCoach(coach || null);
        } else {
          setCoach(null);
        }
      } catch (error) {
        console.error("Error fetching owner/coach:", error);
      }
    };
    
    getOwnerAndCoach();
  }, [teamData]);

  // Get seasons for this team on initial mount
  useEffect(() => {
    getSeasonsPlayed();
  }, [getSeasonsPlayed]);

  // Decide which season to display once 'seasons' is loaded
  useEffect(() => {
    if (seasons.length === 0) return;
    
    console.log("Setting season based on seasons array:", seasons);
    const querySeason = searchParams.get('season');
    const parsedQuerySeason = querySeason ? parseInt(querySeason, 10) : NaN;

    if (!isNaN(parsedQuerySeason) && seasons.includes(parsedQuerySeason)) {
      setSeason(parsedQuerySeason);
    } else {
      // fallback to the highest season the team has played
      setSeason(Math.max(...seasons));
    }
  }, [seasons, searchParams]);

  // Whenever 'season' changes, fetch the standing for that season
  useEffect(() => {
    if (season === 0) return;
    
    const fetchSeasonData = async () => {
      setSeasonChangeLoading(true);
      await getTeamStanding();
      setSeasonChangeLoading(false);
    };
    
    fetchSeasonData();
  }, [getTeamStanding, season]);

  // Called when the user chooses a different season in the SeasonScroll
  const handleSeasonChange = useCallback((selectedSeason: number) => {
    if (selectedSeason !== season) {
      setSeason(selectedSeason);
    }
  }, [season]);

  // Called when switching tabs in TeamTabs
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  // Utility function with proper type annotations
  const calculateAverageRating = (playerArray: PlayerStats[]): string => {
    if (!playerArray || playerArray.length === 0) return "0.00";
    const totalRating = playerArray.reduce((sum: number, player: PlayerStats) => sum + player.hltv2Rating, 0);
    return (totalRating / playerArray.length).toFixed(2);
  };

  // New utility function to calculate average Faceit ELO
  const calculateAverageFaceitElo = (playerArray: PlayerStats[]): string => {
    if (!playerArray || playerArray.length === 0) return "0";
    
    // Filter out players without account or faceitElo
    const playersWithElo = playerArray.filter(player => player.account && player.account.faceitElo);
    
    if (playersWithElo.length === 0) return "0";
    
    const totalElo = playersWithElo.reduce((sum: number, player: PlayerStats) => 
      sum + (player.account ? player.account.faceitElo : 0), 0);
    
    return Math.round(totalElo / playersWithElo.length).toString();
  };

  // Show full page loading only on initial load
  if (isInitialLoading) {
    return (
      <div className="container text-center py-5">
        <Loading />
        <p className="mt-3">Loading team data...</p>
      </div>
    );
  }

  // Determine which data to display - current or previous during loading
  const displayTeamData = (!teamDataLoading || !prevTeamData) ? teamData : prevTeamData;
  const displayPlayers = (!teamDataLoading || !prevPlayers.length) ? players : prevPlayers;

  return (
    <div className="container">
      {/* Admin/Owner edit button */}
      <div className="text-end">
        {
          (visitor && (visitor.steamID === owner?.steamID || admins.includes(Number(visitor.steamID)))) && (
            <Link to={`/team/${teamId}/edit?season=${season}`} className="btn btn-primary">
              Rediger hold
            </Link>
          )
        }
      </div>

      {/* Scrollable season selector with loading indicator */}
      <SeasonScroll
        seasons={seasons}
        selectedSeason={season}
        onSeasonChange={handleSeasonChange}
        isLoading={seasonChangeLoading || teamDataLoading}
      />

      <hr />

      {/* Team header info */}
      {!displayTeamData ? (
        <div className="text-center mt-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading team data...</span>
          </div>
        </div>
      ) : (
        <div className={`text-center mt-5 mb-2 ${(seasonChangeLoading || teamDataLoading) ? 'team-page-dimmed' : 'team-page-normal'}`}>
          <div className="row">
            <div className="col-7">
<h6>Avg ELO: {calculateAverageFaceitElo(displayPlayers)}</h6>
              <h1>{displayTeamData.teamName}</h1>
              <img src={displayTeamData.teamLogo} alt={displayTeamData.teamName} style={{ width: 200 }} />
              <Link to={`/standing/${season}/${displayTeamData.division}`} className="text-decoration-none">
                <h4>{divisionMappings.get(displayTeamData.division)}</h4>
              </Link>
              {standing && (
                <div>
                  <Link to={`/standing/${season}/${displayTeamData.division}`} className="text-decoration-none">
                    <h5>Placering: {standing.rank}</h5>
                  </Link>
                </div>
              )}
            </div>

            {/* Owner & Coach */}
            <div className="col mt-2">
              <h2>Holdejer {coach && "og Coach"}</h2>

              {/* Responsive row for owner and coach */}
              <div className="row row-cols-1 row-cols-sm-2 g-3 justify-content-center text-center">
                {/* Owner Card */}
                {owner ? (
                  <div className="col">
                    <div className="card">
                      <div className="card-body">
                        <Link to={`/player/${owner.steamID}`} className="text-decoration-none text-black">
                          <PlayerImage id={owner.steamID} width={100} />
                          <h5 className="card-title text-center">{owner.playerName}</h5>
                        </Link>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="col text-center">
                    <p>
                      Der er ingen holdejer på holdet.
                      Skriv til{" "}
                      <a
                        href="https://x.com/realxGuys"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Casper
                      </a>{" "}
                      eller{" "}
                      <a
                        href="https://twitter.com/RealSoerensen"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Soerensen
                      </a>{" "}
                      fra holdets twitter konto for at tilføje en holdejer.
                    </p>
                  </div>
                )}

                {/* Coach Card (only renders if a coach exists) */}
                {coach && (
                  <div className="col">
                    <div className="card">
                      <div className="card-body">
                        <Link to={`/player/${coach.steamID}`} className="text-decoration-none text-black">
                          <PlayerImage id={coach.steamID} width={100} />
                          <h5 className="card-title text-center">{coach.playerName}</h5>
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Twitter and Website links */}
              {displayTeamData.twitter && (
                <div className="mt-2">
                  <h5>Twitter</h5>
                  <a
                    href={`https://twitter.com/${displayTeamData.twitter}`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-info"
                  >
                    @{displayTeamData.twitter}
                  </a>
                </div>
              )}
              {displayTeamData.website && (
                <div className="mt-2">
                  <h5>Hjemmeside</h5>
                  <a
                    href={
                      displayTeamData.website.startsWith("http")
                        ? displayTeamData.website
                        : `https://${displayTeamData.website}`
                    }
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-info"
                  >
                    {displayTeamData.website}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <hr />

      {/* Tabs for players, matches, veto */}
      <TeamTabs onTabChange={handleTabChange} />
      <hr />

      {/* Content area with smooth loading transitions */}
      <div className="team-page-content">
        {activeTab === "players" && (
          <>
            <div className={teamDataLoading ? 'team-page-dimmed' : 'team-page-normal'}>
              <TeamPlayers 
                players={displayPlayers} 
                averageRating={calculateAverageRating(displayPlayers)} 
              />
            </div>
            {teamDataLoading && (
              <div className="team-page-overlay">
                <Loading />
              </div>
            )}
          </>
        )}
        
        {activeTab === "matches" && (
          <>
            <div className={seasonChangeLoading ? 'team-page-dimmed' : 'team-page-normal'}>
              <TeamMatches season={season} />
            </div>
            {seasonChangeLoading && (
              <div className="team-page-overlay">
                <Loading />
              </div>
            )}
          </>
        )}
        
        {activeTab === "veto" && (
          <>
            <div className={seasonChangeLoading ? 'team-page-dimmed' : 'team-page-normal'}>
              <TeamMapStats season={season} />
            </div>
            {seasonChangeLoading && (
              <div className="team-page-overlay">
                <Loading />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TeamPage;